import { Injectable } from '@angular/core';
import { detectHandheldDevice } from './device-detection';

/**
 * Chromium's User-Agent Client Hints. Not in the TypeScript DOM lib yet, and only the one
 * field this service uses is declared.
 */
interface NavigatorUAData {
  readonly mobile?: boolean;
}

/** sessionStorage key for the manual override. See {@link DeviceService.isHandheld}. */
const OVERRIDE_KEY = 'ccp.mobileView';

/** Query parameter that sets the override: `?mobileView=1` forces on, `=0` forces off. */
const OVERRIDE_PARAM = 'mobileView';

/**
 * Device *class* detection — is this a handheld (phone or tablet) or a computer?
 *
 * ## Why this is not ResponsiveService
 *
 * {@link ResponsiveService} answers "how wide is the viewport", which is a different question
 * and must stay that way. A laptop with a narrowed browser window is a small viewport but is
 * still a laptop; a tablet in landscape is a wide viewport but is still something you pick up
 * and point at things. Layout should key off viewport width — that is what breakpoints are
 * for. Only decisions that depend on the physical device belong here.
 *
 * Today there is one such decision: whether a screen may take over the whole viewport and
 * hide the application chrome. That is a device question, not a width question. Stripping the
 * navigation off a laptop because its window is 700px wide would remove the only way to get
 * out of the page, on a machine where the window can be widened again in a second.
 *
 * ## Accuracy
 *
 * User-agent sniffing is unreliable in general, which is why this service exposes device
 * class and nothing more — no feature detection, no version checks, no browser names. The
 * cost of a wrong answer here is cosmetic and recoverable (chrome shown when it could have
 * been hidden, or hidden with an on-screen exit still available), which is the only reason
 * sniffing is acceptable at all. Anything with a real failure cost must use feature detection
 * instead: ask for the camera and handle the rejection, do not ask what device this is.
 */
@Injectable({ providedIn: 'root' })
export class DeviceService {
  /** Resolved once. Device class cannot change without a reload, unlike viewport width. */
  private readonly handheld: boolean;

  constructor() {
    this.handheld = this.readOverride() ?? this.detectHandheld();
  }

  /**
   * True for phones and tablets, false for laptops and desktops.
   *
   * Can be overridden for testing with `?mobileView=1` (or `=0`) on any URL. The override
   * persists for the browser tab, because the parameter is lost as soon as the app navigates
   * and the screens worth testing this way navigate immediately. Testing the handheld layout
   * otherwise means a real device or browser device-emulation, since emulation is the only
   * thing that changes the user agent.
   */
  get isHandheld(): boolean {
    return this.handheld;
  }

  /** True for laptops and desktops. The complement of {@link isHandheld}, spelled out. */
  get isComputer(): boolean {
    return !this.handheld;
  }

  /** Reads the signals off `navigator` and hands them to `detectHandheldDevice`. */
  private detectHandheld(): boolean {
    const nav = window.navigator as Navigator & { userAgentData?: NavigatorUAData };
    return detectHandheldDevice({
      userAgent: nav.userAgent,
      platform: nav.platform,
      maxTouchPoints: nav.maxTouchPoints,
      uaMobile: nav.userAgentData?.mobile,
    });
  }

  /**
   * Reads the override from the URL, falling back to one stored earlier in this tab.
   * Returns null when no override is in play, so the caller can tell "forced off" apart
   * from "not set".
   */
  private readOverride(): boolean | null {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(OVERRIDE_PARAM);
      if (fromUrl !== null) {
        const forced = fromUrl !== '0' && fromUrl !== 'false';
        window.sessionStorage.setItem(OVERRIDE_KEY, String(forced));
        return forced;
      }

      const stored = window.sessionStorage.getItem(OVERRIDE_KEY);
      return stored === null ? null : stored === 'true';
      // Storage throws rather than returning null in Safari private mode and when cookies
      // are blocked. A test-only affordance must not be able to break the app.
    } catch {
      return null;
    }
  }
}
